import { RefObject, useCallback, useEffect, useRef } from 'react';

type OutsideClickEventType = 'click' | 'mousedown';

// Same shape `useRef<T>(null)` returns, so callers can pass any of their own
// refs (or this hook's returned ref) as an extra boundary.
type OutsideClickBoundaryRef<T extends HTMLElement> = RefObject<T | null>;

/**
 * Calls `callback` when a click is observed outside the `ref` element.
 * This currently only supports web.
 * @param callback Outside click handler. For best results, do not use an
 * inline function as callback.
 * @param options.capture Attach the listener in the capture phase.
 * @param options.eventType Which pointer event closes it. Default 'click'.
 * @param options.enabled Skip attaching the listener entirely (e.g. while a
 * popover is closed). Default true.
 * @param options.extraRefs Additional elements to treat as "inside" — for
 * content rendered outside the primary ref's DOM subtree (e.g. via a portal),
 * such as a popover anchored to a button that lives elsewhere in the tree.
 * @returns Ref to determine whether the click is inside or outside.
 */
export const useOutsideClick = <T extends HTMLElement = HTMLElement>(
  callback: (event: MouseEvent) => void,
  options?: {
    capture?: boolean;
    eventType?: OutsideClickEventType;
    enabled?: boolean;
    extraRefs?: OutsideClickBoundaryRef<HTMLElement>[];
  }
): RefObject<T | null> => {
  const ref = useRef<T | null>(null);
  const useCapture = options?.capture ?? false;
  const eventType = options?.eventType ?? 'click';
  const enabled = options?.enabled ?? true;

  // Read via a ref rather than a `useEffect` dependency: an inline
  // `extraRefs: [someRef]` at the call site is a new array every render, and
  // putting it in the deps array would tear the listener down and reattach it
  // on every render. The ref is always current by the time a click fires, so
  // there's no staleness trade-off for keeping it out of the deps below.
  const extraRefsRef = useRef(options?.extraRefs);
  extraRefsRef.current = options?.extraRefs;

  useEffect(() => {
    if (!enabled) return undefined;

    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node | null;

      // A missing ref (not yet mounted, or a test renderer that doesn't
      // populate host refs) is treated as "not inside" rather than skipped —
      // matching the original raw-listener behavior this hook replaced
      // (`containerRef.current?.contains(...)`), which several existing
      // tests assert on.
      if (!target) return;
      if (ref.current?.contains(target)) return;
      if (
        extraRefsRef.current?.some((extraRef) =>
          extraRef.current?.contains(target)
        )
      ) {
        return;
      }

      callback(event);
    };

    // Omit the capture argument entirely when unused, rather than passing an
    // explicit `false` — matches the 2-arg addEventListener/removeEventListener
    // calls the original raw listeners made, which some existing tests assert
    // on via toHaveBeenCalledWith.
    if (useCapture) {
      window?.document?.addEventListener(eventType, handleClick, true);
    } else {
      window?.document?.addEventListener(eventType, handleClick);
    }

    return () => {
      if (useCapture) {
        window?.document?.removeEventListener(eventType, handleClick, true);
      } else {
        window?.document?.removeEventListener(eventType, handleClick);
      }
    };
  }, [ref, callback, useCapture, eventType, enabled]);

  return ref;
};

/**
 * Subscribes to a document/window mouse event without any containment check.
 * Use this for the rare listener that isn't scoped to "outside a ref" at all
 * (e.g. dismiss on any click anywhere, or a caller that needs to run its own
 * containment logic on every event). Prefer `useOutsideClick` when a single
 * boundary element is enough — this is the escape hatch, not the default.
 */
export const useGlobalPointerListener = (
  eventType: OutsideClickEventType,
  handler: (event: MouseEvent) => void,
  options?: {
    capture?: boolean;
    enabled?: boolean;
    target?: 'document' | 'window';
  }
): void => {
  const useCapture = options?.capture ?? false;
  const enabled = options?.enabled ?? true;
  const useWindowTarget = options?.target === 'window';

  useEffect(() => {
    if (!enabled) return undefined;

    // Branched rather than resolved to a single `Window | Document` variable
    // so TS can still match each side's own overload set (a unioned target
    // loses the literal-eventType overload and widens the listener param to
    // plain `Event`).
    if (useWindowTarget) {
      window?.addEventListener(eventType, handler, useCapture);
      return () => {
        window?.removeEventListener(eventType, handler, useCapture);
      };
    }

    window?.document?.addEventListener(eventType, handler, useCapture);
    return () => {
      window?.document?.removeEventListener(eventType, handler, useCapture);
    };
  }, [eventType, handler, useCapture, enabled, useWindowTarget]);
};

/**
 * Convenience wrapper for the single most common shape built on top of
 * `useOutsideClick`: a popover/popup that closes itself on an outside click
 * while `isOpen`. `onClose` is read via a ref internally, so callers don't
 * need to memoize it themselves.
 */
export const useCloseOnOutsideClick = <T extends HTMLElement = HTMLElement>(
  isOpen: boolean,
  onClose: () => void,
  options?: {
    capture?: boolean;
    eventType?: OutsideClickEventType;
    extraRefs?: OutsideClickBoundaryRef<HTMLElement>[];
  }
): RefObject<T | null> => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const handleOutsideClick = useCallback(() => {
    onCloseRef.current();
  }, []);

  return useOutsideClick<T>(handleOutsideClick, {
    ...options,
    enabled: isOpen,
  });
};

/**
 * Convenience wrapper for the checklist-item shape: outside click commits or
 * discards, Escape cancels, both gated by the same `enabled` flag. `onEscape`
 * and `onOutsideClick` are read via refs internally, so callers don't need to
 * memoize them themselves.
 */
export const useOutsideClickAndEscape = <T extends HTMLElement = HTMLElement>(
  enabled: boolean,
  onOutsideClick: () => void,
  onEscape: () => void
): RefObject<T | null> => {
  const onOutsideClickRef = useRef(onOutsideClick);
  onOutsideClickRef.current = onOutsideClick;
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  const handleOutsideClick = useCallback(() => {
    onOutsideClickRef.current();
  }, []);

  const containerRef = useOutsideClick<T>(handleOutsideClick, {
    eventType: 'mousedown',
    enabled,
  });

  useEffect(() => {
    if (!enabled) return undefined;

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onEscapeRef.current();
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [enabled]);

  return containerRef;
};
